import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import NotificationCenter from '../NotificationCenter/NotificationCenter';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { getAppCtaHref } from '../../config/appConfig';
import styles from './Navbar.module.css';

const PICKS_PATH = '/discover?feed=aluvo-picks';

// Rankings opens the creator for guests and your own rankings once signed in.
// `isActive` decides the highlight from the current location (Discover and
// Aluvo Picks share a page, told apart by the ?feed= query).
function getNavLinks(user) {
    return [
        {
            label: 'Discover',
            to: '/discover',
            isActive: ({ pathname, search }) => pathname.startsWith('/discover') && !search.includes('feed=aluvo-picks'),
        },
        {
            label: 'Aluvo Picks',
            to: PICKS_PATH,
            isActive: ({ pathname, search }) => pathname.startsWith('/discover') && search.includes('feed=aluvo-picks'),
        },
        { label: 'Collections', to: '/collections', isActive: ({ pathname }) => pathname.startsWith('/collections') },
        {
            label: 'Rankings',
            to: user ? '/me/rankings' : '/rankings/new',
            isActive: ({ pathname }) => pathname.startsWith('/rankings') || pathname.startsWith('/me/rankings'),
        },
        { label: 'About', to: '/about', isActive: ({ pathname }) => pathname === '/about' },
    ];
}

function ThemeToggle() {
    const { isDark, toggleTheme } = useTheme();
    return (
        <button
            className={styles.themeToggle}
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
            {isDark ? '☀️' : '🌙'}
        </button>
    );
}

function Navbar() {
    const { user, logout } = useAuth();
    // Desktop and phone headers are both in the DOM (CSS hides one); render a
    // single bell so the unread count is only polled once.
    const isNarrow = useMediaQuery('(max-width: 768px)');
    const navigate = useNavigate();
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        setDropdownOpen(false);
        setMenuOpen(false);
    }, [location.pathname]);

    const handleAppCtaClick = () => {
        setDropdownOpen(false);
        setMenuOpen(false);
        const href = getAppCtaHref();
        if (href) {
            window.open(href, '_blank', 'noopener,noreferrer');
        }
    };

    const appCtaHref = getAppCtaHref();

    const handleLinkClick = () => {
        setMenuOpen(false);
        setDropdownOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleLogout = async () => {
        setMenuOpen(false);
        setDropdownOpen(false);
        await logout();
        navigate('/');
    };

    const navLinks = getNavLinks(user);
    const username = user?.username || user?.displayName || 'User';
    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

    return (
        <header className={`${styles.navbar} ${scrolled ? styles.scrolled : ''}`}>
            <div className={`container ${styles.inner}`}>
                {/* Logo */}
                <Link to="/" className={styles.logo} onClick={handleLinkClick}>
                    <span className={styles.logoMark}>◈</span>
                    <span className={styles.logoText}>Aluvo</span>
                </Link>

                {/* Desktop nav */}
                <nav className={styles.desktopNav} aria-label="Main">
                    {navLinks.map((link) => {
                        const active = link.isActive(location);
                        return (
                            <Link
                                key={link.label}
                                to={link.to}
                                className={`${styles.navLink} ${active ? styles.active : ''}`}
                                aria-current={active ? 'page' : undefined}
                                onClick={handleLinkClick}
                            >
                                {link.label}
                            </Link>
                        );
                    })}
                </nav>

                {/* Desktop auth */}
                <div className={styles.desktopCta}>
                    <ThemeToggle />
                    {user ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {!isNarrow && <NotificationCenter />}
                            <div className={styles.userMenu} ref={dropdownRef}>
                                <button
                                    className={styles.userMenuBtn}
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                >
                                    <div className={styles.avatarWrap}>
                                        {user.avatarUrl ? (
                                            <img src={user.avatarUrl} alt={username} className={styles.avatarImg} />
                                        ) : (
                                            <span className={styles.avatarInitial}>
                                                {(username?.[0] ?? '?').toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                    <span className={styles.username}>@{username}</span>
                                    <span className={styles.dropdownArrow}>▼</span>
                                </button>

                                {dropdownOpen && (
                                    <div className={styles.dropdown}>
                                        <Link
                                            to="/me"
                                            className={styles.dropdownItem}
                                            onClick={handleLinkClick}
                                        >
                                            <span className={styles.dropdownIcon}>👤</span>
                                            My Profile
                                        </Link>
                                        <Link
                                            to="/top-movies"
                                            className={styles.dropdownItem}
                                            onClick={handleLinkClick}
                                        >
                                            <span className={styles.dropdownIcon}>🎬</span>
                                            My Top Movies
                                        </Link>
                                        <Link
                                            to="/feedback"
                                            className={styles.dropdownItem}
                                            onClick={handleLinkClick}
                                        >
                                            <span className={styles.dropdownIcon}>💬</span>
                                            Give Feedback
                                        </Link>
                                        {isAdmin && (
                                            <Link
                                                to="/admin"
                                                className={`${styles.dropdownItem} ${styles.adminLink}`}
                                                onClick={handleLinkClick}
                                            >
                                                <span className={styles.dropdownIcon}>⚙️</span>
                                                Admin Dashboard
                                            </Link>
                                        )}
                                        {appCtaHref && (
                                            <button
                                                className={`${styles.dropdownItem} ${styles.appCtaItem}`}
                                                onClick={handleAppCtaClick}
                                            >
                                                <span className={styles.dropdownIcon}>📱</span>
                                                <span>
                                                    Get the App
                                                    <span className={styles.dropdownSubtext}>Reminders, alerts &amp; faster sharing</span>
                                                </span>
                                            </button>
                                        )}
                                        <button
                                            className={styles.dropdownItem}
                                            onClick={handleLogout}
                                        >
                                            <span className={styles.dropdownIcon}>🚪</span>
                                            Sign Out
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={styles.authBtns}>
                            <Link to="/login" className={styles.signInBtn}>Sign In</Link>
                            <Link to="/register" className={styles.signUpBtn}>Sign Up</Link>
                        </div>
                    )}
                </div>

                {/* Mobile: theme toggle + notifications + hamburger */}
                <div className={styles.mobileRight}>
                    <ThemeToggle />
                    {user && isNarrow && <NotificationCenter />}
                    <button
                        className={`${styles.hamburger} ${menuOpen ? styles.open : ''}`}
                        onClick={() => setMenuOpen((o) => !o)}
                        aria-label="Toggle menu"
                        aria-expanded={menuOpen}
                    >
                        <span />
                        <span />
                        <span />
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {menuOpen && (
                <div className={styles.mobileMenu}>
                    {navLinks.map((link) => (
                        <Link
                            key={link.label}
                            to={link.to}
                            className={styles.mobileLink}
                            aria-current={link.isActive(location) ? 'page' : undefined}
                            onClick={handleLinkClick}
                        >
                            {link.label}
                        </Link>
                    ))}
                    {user ? (
                        <>
                            <Link to="/me" className={styles.mobileLink} onClick={handleLinkClick}>
                                My Profile
                            </Link>
                            <Link to="/top-movies" className={styles.mobileLink} onClick={handleLinkClick}>
                                My Top Movies
                            </Link>
                            <Link to="/feedback" className={styles.mobileLink} onClick={handleLinkClick}>
                                Give Feedback
                            </Link>
                            {isAdmin && (
                                <Link to="/admin" className={`${styles.mobileLink} ${styles.mobileAdminLink}`} onClick={handleLinkClick}>
                                    ⚙️ Admin Dashboard
                                </Link>
                            )}
                            <button className={styles.mobileSignOut} onClick={handleLogout}>
                                Sign Out
                            </button>
                        </>
                    ) : (
                        <div className={styles.mobileAuthBtns}>
                            <Link to="/login" className={styles.mobileSignIn} onClick={handleLinkClick}>Sign In</Link>
                            <Link to="/register" className={styles.mobileSignUp} onClick={handleLinkClick}>Sign Up</Link>
                        </div>
                    )}
                </div>
            )}
        </header>
    );
}

export default Navbar;
